import { defineComponent as _defineComponent } from 'vue'
import HiddenHorizontalScrollbar from '@/components/HiddenHorizontalScrollbar.vue';
import { afterNextPaint } from '@/helpers/async-helper';
import { useI18n } from '@/helpers/composables/useI18n';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { useUserStore } from '@/stores/pinia/useUserStore';
import { BuyBoxFilterOptions } from '@/stores/useUserStoreTypes';
import { MonetizationType } from '@/@types/graphql-types';
import { useExperiment } from '@/libraries/experiment';
import { useMounted } from '@vueuse/core';
import { computed } from 'vue';


export default /*#__PURE__*/_defineComponent({
  __name: 'TitleDetailJumpLinksV1',
  props: {
    visibleSectionState: null,
    container: { default: null },
    useListBuybox: { type: Boolean }
  },
  setup(__props: any) {

const props = __props as { visibleSectionState: Record<string, boolean>, container: HTMLElement | null, useListBuybox?: boolean };



const { tc } = useI18n();
const { setBuyboxPresentationType, setNewBuyboxFilterType, setBuyboxFilterTypeOptimistic } = useUserStore();

const isMounted = useMounted();

// EXP_TITLE_PAGE_CATEGORIZATION
const { activeVariantWithControlGroup: titlePageCategorizationVariant } = useExperiment(
	'EXP_TITLE_PAGE_CATEGORIZATION'
);
// EXP_TITLE_PAGE_CATEGORIZATION

const links = computed<
	{
		id: string;
		text: string;
		callback?: (parent: HTMLElement | Document) => void;
	}[]
>(() => {
	const baseLinks = [
		{
			id: 'where-to-watch',
			text: tc('WEBAPP_WHERE_TO_WATCH'),
			callback: async _ => {
				if (!isMounted.value) {
					return;
				}
				// updating the buybox is expensive.
				// that's why we split it up in 1) optimistic UI update and 2) defer the expensive store update
				setBuyboxFilterTypeOptimistic(BuyBoxFilterOptions.ALL);
				// INP case: artificially give the JS thread time to breathe for upcoming scrolling
				// note: we already have an afterNextPaint in the scrollTo handler, so this is strictly speaking not necessary - but to avoid regression we keep it
				await afterNextPaint();

				setBuyboxPresentationType({ presentationType: null });
				setNewBuyboxFilterType(BuyBoxFilterOptions.ALL);
			},
		},
		{
			id: 'regular-buybox',
			text: tc('WEBAPP_WATCH_FOR_FREE'),
			callback: async _ => {
				if (!isMounted.value) {
					return;
				}
				// updating the buybox is expensive.
				// that's why we split it up in 1) optimistic UI update and 2) defer the expensive store update
				setBuyboxFilterTypeOptimistic(BuyBoxFilterOptions.FREE);
				// INP case: artificially give the JS thread time to breathe for upcoming scrolling
				// note: we already have an afterNextPaint in the scrollTo handler, so this is strictly speaking not necessary - but to avoid regression we keep it
				await afterNextPaint();

				setBuyboxPresentationType({ presentationType: MonetizationType.Free });
				setNewBuyboxFilterType(BuyBoxFilterOptions.FREE);
			},
		},
		{
			id: 'episodes-lists',
			text: tc('WEBAPP_EPISODES'),
		},
		{
			id: 'synopsis',
			text: tc('WEBAPP_SYNOPSIS'),
		},
		{
			id: 'why-to-watch',
			text: tc('WEBAPP_WHY_TO_WATCH'),
		},
		{
			id: 'trailers',
			text: tc('WEBAPP_TRAILERS_SHORT'),
		},
		{
			id: 'streaming-chart',
			text: tc('WEBAPP_STREAMING_CHARTS'),
		},
		{
			id: 'similar-content',
			text: tc('WEBAPP_SIMILAR_TITLES'),
		},
	];

	// EXP_TITLE_PAGE_CATEGORIZATION: variant_2 moves the "Watch for free" jump link to the end of the list
	if (titlePageCategorizationVariant.value === 'variant_2') {
		const freeLinkIndex = baseLinks.findIndex(link => link.id === 'regular-buybox');
		if (freeLinkIndex !== -1) {
			const [freeLink] = baseLinks.splice(freeLinkIndex, 1);
			baseLinks.push(freeLink);
		}
	}
	// EXP_TITLE_PAGE_CATEGORIZATION

	return baseLinks;
});

const isSectionHidden = (id: string) => {
	return props.visibleSectionState[id] === false;
};

async function scrollTo(id: string, disableTracking = false) {
	// INP case: focusing the clicked element is for google enough to consider as "next paint".
	// that's why we split up:
	// 1) focusing the clicked element
	// 2) and calculating where the browser has to move to
	await afterNextPaint();

	/**
	 * We are setting where-to-watch as the parent for regular-buybox because, on SeasonPages and ShowPages,
	 * we have multiple buyboxes shown simultaneously. Doing this ensures that we scroll to the correct buybox.
	 */
	const container = props.container || document;
	const parent = id === 'regular-buybox' ? container.querySelector('#where-to-watch') : container;
	const element = parent?.querySelector(`#${id}`);
	if (element) {
		element.scrollIntoView({ behavior: 'smooth' });
	}
	links.value.find(link => link.id === id)?.callback?.(container);

	if (!disableTracking) {
		const label = id === 'regular-buybox' ? 'watch-for-free' : id;
		const normalizedLabel = label.replaceAll('-', '_');
		TrackingHelper.trackEvent('userinteraction', {
			action: 'jumplink_clicked',
			label: normalizedLabel,
		});
	}
}

return { __sfc: true,props, tc, setBuyboxPresentationType, setNewBuyboxFilterType, setBuyboxFilterTypeOptimistic, isMounted, titlePageCategorizationVariant, links, isSectionHidden, scrollTo, HiddenHorizontalScrollbar }
}

})